import { regularApiClient } from "../utils/kiota-client";
import type {
  CoolingRegimeProductGroupDto,
  GenusDto,
  ProductGroupDto,
} from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/models/index";

export interface CoolingRegimeProductGroupListResult {
  value: CoolingRegimeProductGroupDto[];
  error?: {
    message: string;
    statusCode?: number;
  };
}

export interface ProductGroupListResult {
  value: ProductGroupDto[];
  "@odata.count"?: number;
  error?: {
    message: string;
    statusCode?: number;
  };
}

export interface CoolingRegimeProductGroupFilters {
  page?: number;
  pageSize?: number;
  registration?: string;
  registrationCode?: string;
  productGroupName?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Loads product groups data with nested cooling regime product group from the API using the Kiota client
 */
export async function loadCoolingRegimeProductGroups(
  filters: CoolingRegimeProductGroupFilters = {},
): Promise<ProductGroupListResult> {
  try {
    const {
      page = 1,
      pageSize = 10,
      registration,
      registrationCode,
      productGroupName,
      startDate,
      endDate,
    } = filters;

    // Build filter query for Product Groups endpoint
    const filterParts: string[] = [];
    if (registrationCode) {
      filterParts.push(`code eq ${registrationCode}`);
    }
    if (productGroupName) {
      filterParts.push(`contains(tolower(name), tolower('${productGroupName}'))`);
    }
    if (startDate) {
      filterParts.push(`startDate ge ${startDate}`);
    }
    if (endDate) {
      filterParts.push(`endDate le ${endDate}`);
    }

    const queryConfig = {
      queryParameters: {
        count: true,
        top: pageSize,
        skip: (page - 1) * pageSize,
        filter: filterParts.length > 0 ? filterParts.join(" and ") : undefined,
        orderby: "code desc",
      },
    };

    const data = await regularApiClient.api
      .vWithVersion("1")
      .productGroups.get(queryConfig);

    // Kiota may store @odata properties in additionalData
    // Note: Product Groups API doesn't return a total count, so we estimate
    const count =
      (data as any).additionalData?.["@odata.count"] ||
      (data as any)["@odata.count"] ||
      (data as any)["count"];
    const dataLength = (data as any).value?.length || data?.length || 0;

    // If API doesn't return count, estimate based on page size
    const estimatedCount =
      count !== undefined
        ? count
        : dataLength === pageSize
          ? (page + 1) * pageSize
          : page * pageSize - (pageSize - dataLength);

    return {
      value: (data as any).value || data || [],
      "@odata.count": estimatedCount,
    };
  } catch (error) {
    console.error("Failed to load product groups:", error);
    return {
      value: [],
      error: {
        message:
          error instanceof Error ? error.message : "Failed to load product groups",
        statusCode: 500,
      },
    };
  }
}

/**
 * Interface for updating cooling regime product group temperatures
 */
export interface UpdateCoolingRegimeProductGroupTemperaturesPayload {
  coolingRegimeProductGroupId: number;
  shortTermMainCoolingTemperatureId?: number | null;
  shortTermFallbackCoolingTemperatureId?: number | null;
  longTermMainCoolingTemperatureId?: number | null;
  longTermFallbackCoolingTemperatureId?: number | null;
  shortTermDisplacementMainCoolingTemperatureId?: number | null;
  shortTermDisplacementFallbackCoolingTemperatureId?: number | null;
  longTermDisplacementMainCoolingTemperatureId?: number | null;
  longTermDisplacementFallbackCoolingTemperatureId?: number | null;
}

/**
 * Interface for updating cooling regime product group priorities
 */
export interface UpdateCoolingRegimeProductGroupPrioritiesPayload {
  coolingRegimeProductGroupId: number;
  shortTermCoolingPriorityId?: number | null;
  longTermCoolingPriorityId?: number | null;
  /** Y/N flag for relative humidity indication */
  relativeHumidityIndication?: string | null;
}

/**
 * Update cooling temperatures for a cooling regime product group
 */
export async function updateCoolingRegimeProductGroupTemperatures(
  payload: UpdateCoolingRegimeProductGroupTemperaturesPayload,
): Promise<CoolingRegimeProductGroupDto> {
  try {
    const { coolingRegimeProductGroupId, ...updateData } = payload;

    // First, get the current data
    const currentData = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byId(coolingRegimeProductGroupId)
      .get();

    if (!currentData) {
      throw new Error("Cooling regime product group not found");
    }

    const productGroupCode = currentData.productGroupCode;
    if (productGroupCode === undefined || productGroupCode === null) {
      throw new Error("Missing productGroupCode on cooling regime product group");
    }

    // Merge current data with updates
    const updatedData = {
      ...currentData,
      coolingTemperatureCodeShortTerm:
        updateData.shortTermMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeShortTerm,
      coolingTemperatureCodeLongTerm:
        updateData.longTermMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeLongTerm,
      coolingTemperatureCodeShortTermDisplacement:
        updateData.shortTermDisplacementMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeShortTermDisplacement,
      coolingTemperatureCodeLongTermDisplacement:
        updateData.longTermDisplacementMainCoolingTemperatureId ??
        currentData.coolingTemperatureCodeLongTermDisplacement,
    };

    // Send PUT request
    await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byId(coolingRegimeProductGroupId)
      .put(updatedData);

    // Fetch and return the updated data by genus code
    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byCode.byCode(productGroupCode)
      .get({
        queryParameters: {
          includeAll: true,
        },
      });

    if (!response) {
      throw new Error("Failed to fetch updated cooling regime product group");
    }

    return response;
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to update cooling regime product group temperatures",
    );
  }
}

/**
 * Update cooling priorities for a cooling regime product group
 */
export async function updateCoolingRegimeProductGroupPriorities(
  payload: UpdateCoolingRegimeProductGroupPrioritiesPayload,
): Promise<CoolingRegimeProductGroupDto> {
  try {
    const { coolingRegimeProductGroupId, ...updateData } = payload;

    // First, get the current data
    const currentData = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byId(coolingRegimeProductGroupId)
      .get();

    if (!currentData) {
      throw new Error("Cooling regime product group not found");
    }

    const productGroupCode = currentData.productGroupCode;
    if (productGroupCode === undefined || productGroupCode === null) {
      throw new Error("Missing productGroupCode on cooling regime product group");
    }

    // Merge current data with updates
    const updatedData = {
      ...currentData,
      coolingPriorityCodeShortTerm:
        updateData.shortTermCoolingPriorityId ??
        currentData.coolingPriorityCodeShortTerm,
      coolingPriorityCodeLongTerm:
        updateData.longTermCoolingPriorityId ??
        currentData.coolingPriorityCodeLongTerm,
      relativeHumidityIndication:
        updateData.relativeHumidityIndication ??
        currentData.relativeHumidityIndication,
    };

    // Send PUT request
    await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byId(coolingRegimeProductGroupId)
      .put(updatedData);

    // Fetch and return the updated data by product group code
    const response = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byCode.byCode(productGroupCode)
      .get({
        queryParameters: {
          includeAll: true,
        },
      });

    if (!response) {
      throw new Error("Failed to fetch updated cooling regime product group");
    }

    return response;
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to update cooling regime product group priorities",
    );
  }
}

/**
 * Delete a cooling regime product group by ID
 */
export async function deleteCoolingRegimeProductGroup(
  coolingRegimeProductGroupId: number,
): Promise<void> {
  try {
    await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byId(coolingRegimeProductGroupId)
      .delete();
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to delete cooling regime product group",
    );
  }
}

/**
 * Create a new cooling regime product group with template defaults
 */
export interface CreateCoolingRegimeProductGroupPayload {
  productGroupCode: number;
  applicationCode?: number | null;
  relativeHumidityIndication?: string | null; // 'J' | 'N' | null
  // Optional initial codes; when omitted, backend will treat as unspecified
  coolingTemperatureCodeShortTerm?: number | null;
  coolingTemperatureCodeLongTerm?: number | null;
  coolingTemperatureCodeShortTermDisplacement?: number | null;
  coolingTemperatureCodeLongTermDisplacement?: number | null;
  coolingPriorityCodeShortTerm?: number | null;
  coolingPriorityCodeLongTerm?: number | null;
}

export async function createCoolingRegimeProductGroup(
  payload: CreateCoolingRegimeProductGroupPayload,
): Promise<CoolingRegimeProductGroupDto> {
  const {
    productGroupCode,
    applicationCode = 1,
    relativeHumidityIndication = "N",
    coolingTemperatureCodeShortTerm = null,
    coolingTemperatureCodeLongTerm = null,
    coolingTemperatureCodeShortTermDisplacement = null,
    coolingTemperatureCodeLongTermDisplacement = null,
    coolingPriorityCodeShortTerm = null,
    coolingPriorityCodeLongTerm = null,
  } = payload;

  try {
    const requestBody: Record<string, unknown> = {
      productGroupCode,
      // Include multiple casings to be robust against server binders
      applicationCode,
      relativeHumidityIndication,
      coolingTemperatureCodeShortTerm,
      coolingTemperatureCodeLongTerm,
      coolingTemperatureCodeShortTermDisplacement,
      coolingTemperatureCodeLongTermDisplacement,
      coolingPriorityCodeShortTerm,
      coolingPriorityCodeLongTerm,
    } as any;

    // Helpful debug: inspect payload during development
    try {
      // eslint-disable-next-line no-console
      console.debug("[createCoolingRegimeProductGroup] POST payload", requestBody);
    } catch {}

    await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.post(requestBody);

    const created = await regularApiClient.api
      .vWithVersion("1")
      .coolingRegimeProductGroup.byCode.byCode(productGroupCode)
      .get({
        queryParameters: {
          includeAll: true,
        },
      });

    if (!created) {
      throw new Error("Failed to fetch created cooling regime product group");
    }
    return created as CoolingRegimeProductGroupDto;
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to create cooling regime product group",
    );
  }
}
